export const PAGE_SIZE = 50;

// GPS signal names used to detect latitude/longitude in buslog data
export const LAT_SIGNALS = new Set(['RPPSDBG3P_LatAuth', 'GPSC_PPSLat']);
export const LNG_SIGNALS = new Set(['RPPSDBG2P_LngAuth', 'GPSC_PPSLong']);

// Scale factor for raw CAN GPS values (milliarcseconds) → decimal degrees
// 1 degree = 3,600,000 milliarcseconds
// e.g. 153265242 / 3600000 ≈ 42.5737°
export const GPS_SCALE_FACTOR = 1 / 3600000;

// Continental US bounding box for GPS validation
// Matches backend validation: lat 25–50°, lng -125 to -65°
export const GPS_LAT_RANGE = [25.0, 50.0];
export const GPS_LNG_RANGE = [-125.0, -65.0];

/** Returns true if lat/lng (in degrees) are valid continental US coordinates */
export const isValidGpsCoord = (lat, lng) =>
  lat != null &&
  lng != null &&
  !isNaN(lat) &&
  !isNaN(lng) &&
  lat >= GPS_LAT_RANGE[0] &&
  lat <= GPS_LAT_RANGE[1] &&
  lng >= GPS_LNG_RANGE[0] &&
  lng <= GPS_LNG_RANGE[1];

/**
 * Detect the divisor needed to convert a raw timestamp to seconds.
 * @param {number} timestamp — a representative raw timestamp value
 * @returns {number} divisor such that `timestamp / divisor` ≈ epoch seconds
 */
export const detectTimeDivisor = (timestamp) => {
  const magnitude = Math.log10(Math.abs(timestamp) || 1);
  if (magnitude >= 17) return 1e9;   // Nanoseconds
  if (magnitude >= 14) return 1e6;   // Microseconds
  if (magnitude >= 10) return 1e3;   // Milliseconds
  return 1;                          // Already seconds
};

export const TIME_FILTER_OPTIONS = [
  { value: '5s', label: '5s', ms: 5 * 1000 },
  { value: '10s', label: '10s', ms: 10 * 1000 },
  { value: '20s', label: '20s', ms: 20 * 1000 },
  { value: '30s', label: '30s', ms: 30 * 1000 },
  { value: '1min', label: '1 Min', ms: 60 * 1000 },
  { value: '5min', label: '5 Min', ms: 5 * 60 * 1000 },
  { value: 'all', label: 'All Time', ms: null },
];

export const getSafeTimestamp = (timestamp) => {
  if (!timestamp) return 0;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof timestamp === 'number') return timestamp;
  
  const date = new Date(timestamp);
  if (!isNaN(date.getTime())) return date.getTime();
  
  const num = Number(timestamp);
  if (!isNaN(num)) return num;
  
  return 0;
};

/**
 * findClosestByTime — generic binary search over any sorted array.
 *
 * Returns the element whose timestamp is **nearest** to targetTs.
 *
 * @param {Array}    arr      — sorted array to search
 * @param {number}   targetTs — target timestamp in the same units as getTs returns
 * @param {Function} getTs    — (item) => number  accessor for the item's timestamp
 * @returns the closest element, or null if arr is empty
 */
export const findClosestByTime = (arr, targetTs, getTs) => {
  if (!arr || arr.length === 0) return null;
  let lo = 0;
  let hi = arr.length - 1;

  // Binary search: find largest index where getTs(arr[lo]) <= targetTs
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (getTs(arr[mid]) <= targetTs) lo = mid;
    else hi = mid - 1;
  }

  // return whichever is closer in absolute distance to targetTs.
  if (lo + 1 < arr.length) {
    const floorDist = Math.abs(targetTs - getTs(arr[lo]));
    const ceilDist  = Math.abs(getTs(arr[lo + 1]) - targetTs);
    if (ceilDist < floorDist) return arr[lo + 1];
  }

  return arr[lo];
};

/**
 * findAtPlaybackTime — finds the element in a sorted array nearest to a
 * playback position, handling unit mismatch
 *
 * @param {Array}    arr            — sorted array to search
 * @param {number}   currentTimeSec — elapsed playback time in seconds
 * @param {Function} getTs          — (item) => raw timestamp number
 * @param {number}   globalStartTs  — file start timestamp in nanoseconds
 * @returns the closest element, or null if arr is empty or too far away
 */
const MAX_DISTANCE_SEC = 0.1;

export const findAtPlaybackTime = (arr, currentTimeSec, getTs, globalStartTs) => {
  if (!arr || arr.length === 0) return null;

  // Detect what unit the data timestamps are in
  const dataDivisor = detectTimeDivisor(getTs(arr[0]));
  // Convert the ns-epoch origin into the data's unit
  const originInDataUnits = globalStartTs / (1e9 / dataDivisor);
  const targetTs = originInDataUnits + currentTimeSec * dataDivisor;
  const match = findClosestByTime(arr, targetTs, getTs);
  if (!match) return null;

  // Reject if the closest point is more than MAX_DISTANCE_SEC away
  const distInUnits = Math.abs(getTs(match) - targetTs);
  if (distInUnits / dataDivisor > MAX_DISTANCE_SEC) return null;

  return match;
};

/**
 * Format a raw epoch-ms timestamp → "MM-DD-YYYY HH:mm:ss.SSS TZ"
 * Appends the browser's short timezone abbreviation (e.g. EST, PST, UTC).
 */
export const formatAbsoluteTimestamp = (time) => {
  const PLACEHOLDER = '--/--/---- --:--:--.--- ---';
  if (time == null || time === 0) return PLACEHOLDER;
  const d = new Date(time);
  if (isNaN(d.getTime())) return PLACEHOLDER;
  const MM = String(d.getMonth() + 1).padStart(2, '0');
  const DD = String(d.getDate()).padStart(2, '0');
  const YYYY = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  const ms = String(d.getMilliseconds()).padStart(3, '0');
  // Extract short timezone abbreviation (e.g. "EST", "PST")
  const tz = Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
    .formatToParts(d)
    .find((p) => p.type === 'timeZoneName')?.value || '';
  return `${MM}-${DD}-${YYYY} ${hh}:${mm}:${ss}.${ms} ${tz}`;
};

/**
 * getPlaybackTimestamp — converts an elapsed playback time into a formatted
 * absolute timestamp string using the authoritative file start time.
 *
 * @param {number} globalStartTs  — file start timestamp in nanoseconds (from backend)
 * @param {number} currentTimeSec — elapsed playback time in seconds
 * @returns {string} formatted absolute timestamp, or the placeholder string
 */
export const getPlaybackTimestamp = (globalStartTs, currentTimeSec) => {
  if (globalStartTs == null || globalStartTs <= 0) return formatAbsoluteTimestamp(null);
  // globalStartTs is nanoseconds → add seconds converted to nanoseconds
  const absoluteNs = globalStartTs + currentTimeSec * 1e9;
  // Convert nanoseconds → milliseconds for Date()
  const absoluteMs = absoluteNs / 1e6;
  return formatAbsoluteTimestamp(absoluteMs);
};

/**
 * Format a time-in-seconds value → "mm:ss.SS" (2 decimal places).
 */
export const formatRelativeTime = (seconds) => {
  if (seconds == null || isNaN(seconds)) return '00:00.00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${secs.toFixed(2).padStart(5, '0')}`;
};

export const filterDataByTime = (data, filterValue) => {
  if (!filterValue || filterValue === 'all' || !data?.length) return data;
  
  const filterOption = TIME_FILTER_OPTIONS.find(opt => opt.value === filterValue);
  if (!filterOption?.ms) return data;

  let latestTimestamp = -Infinity;
  for (const d of data) {
    const ts = getSafeTimestamp(d.timestamp);
    if (ts > latestTimestamp) latestTimestamp = ts;
  }

  if (latestTimestamp === -Infinity) return data;
  
  let multiplier = detectTimeDivisor(latestTimestamp) / 1000;

  const durationInUnits = filterOption.ms * multiplier;
  const cutoffTime = latestTimestamp - durationInUnits;
  
  return data.filter(d => getSafeTimestamp(d.timestamp) >= cutoffTime);
};

export const formatYAxisValue = (value) => {
  if (Math.abs(value) >= 1000 || (Math.abs(value) < 1 && value !== 0)) {
    return value.toExponential(2);
  }
  return value.toFixed(2);
};
